/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: Moment library.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io'
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Flex,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import DownloadToExcel from 'components/DownloadToExcel'
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import SenderIdDirectoryUpsert from 'components/SenderIdsDirectoryUpsert'
import SenderIdDirectoryDropDownOptions from 'components/SenderIdsDirectoryDropDownOptions'
import SenderIdDirectoryDelete from 'components/SenderIdsDirectoryDelete'


/*
 * STYLES
 */
import { buttonStyle } from './index.style'


/*
 * GRAPHS
 */
import SenderIdDirectoryReadQuery from './__query__/index.senderIdDirectory.read.query'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _successFlags = Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key)

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ items: [] })
  const [senderIdDirectoryToSearch, setSenderIdDirectoryToSearch] = React.useState('')

  const { 'isOpen': isSenderIdDirectoryCreateOpen, 'onOpen': onSenderIdDirectoryCreateOpen, 'onClose': onSenderIdDirectoryCreateClose } = useDisclosure()
  const { 'isOpen': isSenderIdDirectoryUpdateOpen, 'onOpen': onSenderIdDirectoryUpdateOpen, 'onClose': onSenderIdDirectoryUpdateClose } = useDisclosure()
  const { 'isOpen': isSenderIdDirectoryDeleteOpen, 'onOpen': onSenderIdDirectoryDeleteOpen, 'onClose': onSenderIdDirectoryDeleteClose } = useDisclosure()

  const _QuerySenderIdDirectoryRead = useQuery(SenderIdDirectoryReadQuery, {
    'variables': {
      senderIdDirectoryToSearch,
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })

  const _isFirstLoadCompleted = React.useRef(false)
  const _onSenderIdDirectoryToSearchInputChange = React.useRef(_.debounce((value) => setSenderIdDirectoryToSearch(value), 800)).current
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Check if first load is completed.
  if (!_QuerySenderIdDirectoryRead.loading && 0 < _QuerySenderIdDirectoryRead.data?.SenderIdDirectoryRead?.length && !_isFirstLoadCompleted.current) _isFirstLoadCompleted.current = true
  if (!_.every(_.pluck(_QuerySenderIdDirectoryRead.data?.SenderIdDirectoryRead, 'status'), i => _successFlags.includes(i))) _isFirstLoadCompleted.current = false

  React.useEffect(() => {
    const displayNameFilter = filterModel?.items?.find(item => item.field === 'displayName')
    const searchValue = displayNameFilter?.value || ''
    _onSenderIdDirectoryToSearchInputChange(searchValue)
  }, [filterModel])

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      _onSenderIdDirectoryToSearchInputChange.cancel?.()
    }
  }, [])

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No.',
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 150
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'headerAlign': 'center',
      'hide': true
    },
    {
      'field': 'displayName',
      'headerName': 'Display Name',
      'description': 'Display name of the directory.',
      'editable': false,
      'sortable': false,
      'filterable': true,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 300
    },
    {
      'field': 'totalSenderIds',
      'headerName': 'Total SenderIds',
      'description': 'Total sender IDs count.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 280
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last updated date.',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'flex': 1,
      'minWidth': 250
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Actions for directory.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'width': 200,
      'disableColumnMenu': true,
      'renderCell': ({ row }) => <SenderIdDirectoryDropDownOptions
        senderIdDirectoryId={row?.id}
        onSenderIdDirectoryUpdate={onSenderIdDirectoryUpdateOpen}
        onSenderIdDirectoryDelete={onSenderIdDirectoryDeleteOpen}
      />
    }
  ], [])

  const rows = React.useMemo(() => _.compact(_QuerySenderIdDirectoryRead?.data?.SenderIdDirectoryRead?.map((r, index) =>
    _successFlags.includes(r.status) ? ({
      'id': r?.id || String.random(9),
      'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
      'displayName': r?.displayName ?? '-',
      'totalSenderIds': r?._count?.SenderId ?? '-',
      'createdAt': Moment(r?.createdAt).format('YYYY-MM-DD'),
      'updatedAt': Moment(r?.updatedAt).format('YYYY-MM-DD')
    }) : null
  )), [_QuerySenderIdDirectoryRead?.data?.SenderIdDirectoryRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='senderIdsDirectory base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          minH="81vh"
          maxH='81vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex pb='0px' justify='space-between'>
            <Flex gap='12px'>
              <Tooltip label='Create New' fontSize='sm'>
                <Button
                  leftIcon={<IoMdAdd />}
                  onClick={onSenderIdDirectoryCreateOpen}
                  style={buttonStyle}>
                  Create New Folder
                </Button>
              </Tooltip>
            </Flex>
            <DownloadToExcel
              cellsData={_QuerySenderIdDirectoryRead?.data?.SenderIdDirectoryRead?.map((item, __index) =>
                _successFlags.includes(item.status) ? ({
                  'S.No.': `${(__index + (paginationModel.pageSize * paginationModel.page)) + 1}.`,
                  'CreatedAt': Moment(item?.createdAt).format('YYYY-MM-DD'),
                  'UpdatedAt': Moment(item?.updatedAt).format('YYYY-MM-DD'),
                  'Display Name': item.displayName ?? '-',
                  'Total SenderIds': item?._count?.SenderId ?? '-'
                }) : void 0
              )}
              headersData={[
                'S.No.',
                'CreatedAt',
                'UpdatedAt',
                'Display Name',
                'Total SenderIds'
              ].map(i => ({ 'key': i, 'label': i }))}
            />
          </Flex>
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={58}
            rowCount={_.pluck(_QuerySenderIdDirectoryRead?.data?.SenderIdDirectoryRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            disableColumnSorting={true}
            filterModel={filterModel}
            onFilterModelChange={setFilterModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 30, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QuerySenderIdDirectoryRead?.loading}
          />
        </Flex>
        <Modal
          size='md'
          title='Create Directory'
          isOpen={isSenderIdDirectoryCreateOpen}
          onClose={onSenderIdDirectoryCreateClose}>
          <SenderIdDirectoryUpsert isCreateOnly={true} />
        </Modal>
        <Modal
          size='md'
          title='Update Directory'
          isOpen={isSenderIdDirectoryUpdateOpen}
          onClose={onSenderIdDirectoryUpdateClose}>
          <SenderIdDirectoryUpsert />
        </Modal>
        <Modal
          size='md'
          title='Delete Directory'
          isOpen={isSenderIdDirectoryDeleteOpen}
          onClose={onSenderIdDirectoryDeleteClose}>
          <SenderIdDirectoryDelete />
        </Modal>
      </Flex>
    </>
  )
}


/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
